import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from '@/components/ui/accordion';
import { ArrowRight, HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const ZALMA_URL = process.env.REACT_APP_ZALMA_URL || 'https://www.zalma.com.au';

const FAQ_CATEGORIES = [
  {
    title: 'About Zap AI',
    questions: [
      {
        q: 'What is Zap AI?',
        a: 'Zap AI is an Australian software company building AI-powered platforms for service businesses. Our flagship product, Zalma, is built for pet grooming salons. We are working on additional products for adjacent industries.',
      },
      {
        q: 'Where is Zap AI based?',
        a: 'We are based in Australia and serve Australian and New Zealand businesses. Customer data is hosted in Australia and we comply with Australian privacy and data residency expectations.',
      },
      {
        q: 'Who do you serve?',
        a: 'We focus on appointment-based service businesses that have outgrown spreadsheets and basic booking tools but do not have an in-house IT team. Today that means pet grooming salons. Tomorrow it may include vet clinics, dental practices, and allied health.',
      },
      {
        q: 'Is Zap AI a single product or multiple products?',
        a: 'Zap AI is the parent company. Each product (Zalma is the first) is a focused, vertical-specific platform. We deliberately avoid building one-size-fits-all software.',
      },
    ],
  },
  {
    title: 'Products',
    questions: [
      {
        q: 'What products do you offer today?',
        a: (
          <>
            Zalma, our pet grooming salon management platform, is available now. Visit{' '}
            <a href={ZALMA_URL} className="text-blue-600 hover:underline">www.zalma.com.au</a>{' '}
            to learn more or start a free trial.
          </>
        ),
      },
      {
        q: 'What is on the roadmap?',
        a: 'We are researching expansion into vet clinics, dental practices, and other appointment-based service businesses. If your industry is on our shortlist, we would love to hear from you.',
      },
      {
        q: 'Can you build a custom version for my industry?',
        a: 'We do not currently offer custom builds. Our model is to deeply specialise in one vertical at a time and ship a polished product. Drop us a line if you think your industry is a fit and we will keep you posted as we expand.',
      },
    ],
  },
  {
    title: 'Getting started',
    questions: [
      {
        q: 'How do I get started with a Zap AI product?',
        a: (
          <>
            For Zalma, the fastest way is to head to{' '}
            <a href={ZALMA_URL} className="text-blue-600 hover:underline">www.zalma.com.au</a>{' '}
            and sign up for a free trial. You can also book a personalised demo from the home page.
          </>
        ),
      },
      {
        q: 'Do you offer demos?',
        a: 'Yes. Every product includes a "Book a Demo" option in the navigation. A member of our team will walk you through the platform live and answer specific questions about your business.',
      },
      {
        q: 'How is pricing structured?',
        a: 'Pricing is per-product. Zalma uses a monthly subscription with no setup fees. Specific pricing details are shown when you sign up. We do not lock customers into long contracts.',
      },
      {
        q: 'Is there a free trial?',
        a: 'Yes. Zalma includes a 14-day free trial with full functionality. No credit card is required to start.',
      },
    ],
  },
  {
    title: 'Trust and security',
    questions: [
      {
        q: 'How do you protect customer data?',
        a: 'All data is encrypted at rest and in transit. We enforce strict tenant isolation, role-based access controls, and audit logging on sensitive actions. Production systems are accessed only via short-lived credentials.',
      },
      {
        q: 'Where is data hosted?',
        a: 'Customer data is hosted in Australia. We do not move data offshore. Backups are stored in the same region.',
      },
      {
        q: 'Are you compliant with Australian privacy and spam laws?',
        a: 'Yes. We operate under the Australian Privacy Principles, the Privacy Act 1988, and the Spam Act 2003. Marketing emails sent through our platforms include compliant unsubscribe handling and sender identification.',
      },
      {
        q: 'Who can see my data?',
        a: 'Your data is visible only to authorised users in your account. Zap AI staff access customer data only with explicit permission for support purposes, and all access is logged.',
      },
    ],
  },
  {
    title: 'Contact and support',
    questions: [
      {
        q: 'How do I contact support?',
        a: (
          <>
            Email us at <a href="mailto:info@zapai.com.au" className="text-blue-600 hover:underline">info@zapai.com.au</a>.
            Customers on a paid plan also have access to in-app support and priority response.
          </>
        ),
      },
      {
        q: 'What are your support hours?',
        a: 'Email support is monitored Monday to Friday during Australian business hours (Sydney time). We aim to respond within one business day. Critical issues affecting active customers are escalated outside hours.',
      },
      {
        q: 'I have a question that is not on this page.',
        a: (
          <>
            We would love to hear from you. Send us an email at{' '}
            <a href="mailto:info@zapai.com.au" className="text-blue-600 hover:underline">info@zapai.com.au</a>{' '}
            or use the "Book a Demo" link to chat with our team.
          </>
        ),
      },
    ],
  },
];

function DemoModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Demo request submitted! We'll be in touch shortly.");
      setForm({ name: '', email: '', phone: '', message: '' });
      onOpenChange(false);
    }, 1000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Book a Demo
          </DialogTitle>
          <DialogDescription>
            Fill in your details and we'll schedule a personalized demo for your salon.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="demo-name">Name <span className="text-red-500">*</span></Label>
            <Input id="demo-name" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-email">Email <span className="text-red-500">*</span></Label>
            <Input id="demo-email" name="email" type="email" placeholder="you@salon.com" value={form.email} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-phone">Phone <span className="text-red-500">*</span></Label>
            <Input id="demo-phone" name="phone" type="tel" placeholder="+61 4XX XXX XXX" value={form.phone} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-message">Message</Label>
            <Textarea id="demo-message" name="message" placeholder="Tell us about your salon and what you're looking for..." rows={3} value={form.message} onChange={handleChange} />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 font-medium">
            {submitting ? 'Submitting...' : 'Submit Enquiry'} {!submitting && <Send className="ml-2 h-4 w-4" />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FAQHero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white">
      <div className="max-w-4xl mx-auto px-6 pt-20 pb-12 md:pt-24 md:pb-16 text-center">
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-6 rounded-full px-4 py-1.5">
          <HelpCircle className="h-3.5 w-3.5 mr-1.5" /> Help Center
        </Badge>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]" style={{ fontFamily: 'Manrope' }}>
          Frequently Asked<br />
          <span className="text-blue-600">Questions</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
          Common questions about Zap AI, our products, and how we work. Still cannot find what you are looking for? Get in touch.
        </p>
      </div>
    </section>
  );
}

function FAQContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-3xl mx-auto px-6 space-y-12">
        {FAQ_CATEGORIES.map((cat, i) => (
          <div key={i}>
            <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>
              {cat.title}
            </h2>
            <Accordion type="single" collapsible className="w-full space-y-2">
              {cat.questions.map((item, j) => (
                <AccordionItem
                  key={j}
                  value={`${i}-${j}`}
                  className="border border-slate-200/60 rounded-xl px-5 bg-slate-50/40"
                >
                  <AccordionTrigger className="text-base font-medium text-slate-900 hover:no-underline text-left">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </section>
  );
}

function CTABlock({ onBookDemo }) {
  return (
    <section className="py-20 bg-slate-50/60">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Still have questions?
        </h2>
        <p className="mt-4 text-base text-slate-600 max-w-xl mx-auto">
          We are happy to chat. Book a demo, drop us an email, or read more about who we are.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button size="lg" onClick={onBookDemo} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-12 text-base font-semibold shadow-lg">
            Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Link to="/about">
            <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base font-semibold">
              About Zap AI
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function FAQPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);

  return (
    <div data-testid="faq-page" className="min-h-screen bg-white">
      <Navbar onBookDemo={openDemo} />
      <FAQHero />
      <FAQContent />
      <CTABlock onBookDemo={openDemo} />
      <Footer />
      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
